/* eslint-disable @typescript-eslint/no-misused-promises */

import { faMessageSmile } from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, useViewport } from '@tryhackme/thm-ui-components';
import { Suspense, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { SocialName } from 'src/common/page-share';
import { useTrackEventMutation } from 'src/common/slices';
import { useGetPostRoomCompletionFeedbackByEchoQuery } from 'src/features/room/room.slice';
import { StyledConfettiWrapper } from '../completion-modal/completion-modal.styles';
import { PathProgress } from '../completion-modal/components/steps/path-progress';
import { FeedbackModal } from './components/feedback-modal';
import { PostRoomCompletionFeedbackStep } from './components/post-room-completion-feedback-step';
import { PromotePremiumStep } from './components/promote-premium-step';
import { RecordsStep } from './components/records-step';
import { SharingBadgeStep } from './components/sharing-badge-step';
import { SharingStep } from './components/sharing-step';
import { StyledButtonWrapper, StyledCongratulationsFullScreen, StyledContainer, StyledContentWrapper, StyledFooter } from './congratulations-full-screen.styles';
import type { CongratulationsFullScreenProps } from './congratulations-full-screen.types';
import { isFirstFourRooms, useIsPostRoomCompletionFeedbackEnabled, useShouldPromotePremium, useShouldShareBadges } from './hooks/use-experiments';
import { useStepNavigator } from './hooks/use-steps';
export const CongratulationFullScreen = ({
  currentRoom,
  setIsCongratulationsFullScreenOpen,
  isLoading,
  onClose,
  isConfettiVisible,
  setIsConfettiVisible
}: CongratulationsFullScreenProps) => {
  const {
    width
  } = useViewport();
  const [isLeaveFeedbackOpen, setIsLeaveFeedbackOpen] = useState(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);
  const [trackEvent] = useTrackEventMutation();
  const {
    isEnabled: isPromotePremiumEnabled
  } = useShouldPromotePremium(currentRoom);
  const {
    awardedBadge,
    isLoading: isBadgesLoading
  } = useShouldShareBadges(currentRoom);
  const {
    isEnabled: isPostRoomCompletionFeedbackEnabled
  } = useIsPostRoomCompletionFeedbackEnabled(currentRoom?.code);
  const {
    data: postRoomCompletionFeedback,
    isLoading: isPostRoomCompletionFeedbackLoading,
    isError: isPostRoomCompletionFeedbackError
  } = useGetPostRoomCompletionFeedbackByEchoQuery(currentRoom?.code ?? '', {
    skip: !currentRoom?.code || !isPostRoomCompletionFeedbackEnabled
  });
  const isMobile = width < BREAKPOINTS.MOBILE;
  const currentRoomCode = currentRoom?.code ?? '';
  const nextRoomCode = currentRoom?.nextRoom?.code ?? '';
  const {
    stepName,
    isFirstStep,
    isLastStep,
    onNext,
    onPrevious,
    onSkipToStep
  } = useStepNavigator({
    records: true,
    'post-room-completion-feedback': isPostRoomCompletionFeedbackEnabled,
    'social-sharing': true,
    'badge-sharing': !!awardedBadge,
    'promote-premium': isPromotePremiumEnabled,
    'next-room': !!nextRoomCode
  });
  const navigate = useNavigate();
  const handleClose = () => {
    setIsCongratulationsFullScreenOpen(false);
    onClose?.();
  };
  const handleGoBack = () => {
    const isFeedbackStepEnabled = isPostRoomCompletionFeedbackEnabled;
    const isOnSocialSharing = stepName === 'social-sharing';
    const hasFeedbackData = !isPostRoomCompletionFeedbackLoading && postRoomCompletionFeedback?.data;
    const shouldSkipToRecords = isFeedbackStepEnabled && isOnSocialSharing && (!hasFeedbackData || isPostRoomCompletionFeedbackError);
    if (shouldSkipToRecords) {
      onSkipToStep(1);
    } else {
      onPrevious();
    }
  };
  const handleGoToNextRoom = async () => {
    // eslint-disable-next-line no-void
    await trackEvent({
      event: 'click-next-room-button',
      properties: {
        current_room: currentRoom?.code,
        next_room: nextRoomCode,
        clicked_on: 'from_popup'
      }
    });
    navigate(`/jr/${nextRoomCode}`);
  };
  const renderContent = () => {
    switch (stepName) {
      case 'records':
        {
          return <RecordsStep currentRoom={currentRoom} isLoading={isLoading} isFeedbackSent={isFeedbackSent} />;
        }
      case 'post-room-completion-feedback':
        {
          return <PostRoomCompletionFeedbackStep isLoading={isPostRoomCompletionFeedbackLoading} postRoomCompletionFeedback={postRoomCompletionFeedback?.data ?? null} hasError={isPostRoomCompletionFeedbackError || !isPostRoomCompletionFeedbackLoading && !postRoomCompletionFeedback?.data} onAutoAdvance={onNext} roomCode={currentRoomCode} />;
        }
      case 'social-sharing':
        {
          return <SharingStep currentRoom={currentRoom} isLoading={isLoading} socialLinks={{
            [SocialName.twitter]: true,
            [SocialName.facebook]: true,
            [SocialName.linkedin]: true
          }} />;
        }
      case 'badge-sharing':
        {
          return <SharingBadgeStep awardedBadge={awardedBadge} roomCode={currentRoomCode} isLoading={isBadgesLoading} />;
        }
      case 'promote-premium':
        {
          return <PromotePremiumStep isFirstFourRooms={isFirstFourRooms(currentRoom)} />;
        }
      case 'next-room':
        {
          return <StyledContentWrapper data-content="next-room">
            <PathProgress currentRoom={currentRoom} />
          </StyledContentWrapper>;
        }
      default:
        {
          return null;
        }
    }
  };
  const renderLeftFooterButton = () => {
    if (isFirstStep) {
      return <Button variant="outlined" color="secondary" onClick={() => setIsLeaveFeedbackOpen(true)}>
          <FontAwesomeIcon icon={faMessageSmile} /> Leave Feedback
        </Button>;
    }
    return <Button variant="outlined" color="secondary" onClick={handleGoBack} data-sentry-element="Button" data-sentry-component="renderLeftFooterButton" data-sentry-source-file="congratulations-full-screen.tsx">
        <FontAwesomeIcon icon={faArrowLeft} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="congratulations-full-screen.tsx" /> Go back
      </Button>;
  };
  const renderRightFooterButtons = () => {
    const buttons: JSX.Element[] = [];
    if (isLastStep) {
      buttons.push(<Button variant="outlined" color="secondary" onClick={handleClose}>
          Close
        </Button>);
    }
    if (!isLastStep) {
      const isContinueDisabled = stepName === 'post-room-completion-feedback' && isPostRoomCompletionFeedbackLoading;
      buttons.push(<Button variant="outlined" color={stepName === 'promote-premium' ? 'secondary' : 'primary'} onClick={onNext} disabled={isContinueDisabled}>
          {stepName === 'promote-premium' ? 'Continue for free' : 'Continue'}
        </Button>);
    }

    // next room button
    if (isLastStep && nextRoomCode) {
      buttons.push(<Button color="primary" onClick={handleGoToNextRoom}>
          {stepName === 'next-room' ? 'Go to next room' : 'Continue learning'}
        </Button>);
    }
    return <StyledButtonWrapper data-sentry-element="StyledButtonWrapper" data-sentry-component="renderRightFooterButtons" data-sentry-source-file="congratulations-full-screen.tsx">{buttons}</StyledButtonWrapper>;
  };
  return <StyledCongratulationsFullScreen role="dialog" data-sentry-element="StyledCongratulationsFullScreen" data-sentry-component="CongratulationFullScreen" data-sentry-source-file="congratulations-full-screen.tsx">
      {isConfettiVisible && <StyledConfettiWrapper data-testid="room-completion-confetti">
          <Confetti recycle={false} width={width} initialVelocityY={{
        min: 15,
        max: 20
      }} numberOfPieces={400} tweenDuration={10_000} onConfettiComplete={() => setIsConfettiVisible(false)} />
        </StyledConfettiWrapper>}
      <StyledContainer $isMobile={isMobile} data-sentry-element="StyledContainer" data-sentry-source-file="congratulations-full-screen.tsx">
        {renderContent()}
        <StyledFooter data-sentry-element="StyledFooter" data-sentry-source-file="congratulations-full-screen.tsx">
          {renderLeftFooterButton()}
          {renderRightFooterButtons()}
        </StyledFooter>
      </StyledContainer>
      <Suspense fallback={<div>...Loading</div>} data-sentry-element="Suspense" data-sentry-source-file="congratulations-full-screen.tsx">
        <FeedbackModal isOpen={isLeaveFeedbackOpen} onClose={() => {
        setIsLeaveFeedbackOpen(false);
        setIsFeedbackSent(false);
      }} roomCode={currentRoomCode} setIsFeedbackSent={setIsFeedbackSent} data-sentry-element="FeedbackModal" data-sentry-source-file="congratulations-full-screen.tsx" />
      </Suspense>
    </StyledCongratulationsFullScreen>;
};